import {createContext, memo, useMemo} from 'react'
import type {UseTableColumnOptions} from 'react-table'

import type {ColumnModel} from '../../../../models/column-model'
import {useAllColumns} from '../../../../state-providers/columns/use-all-columns'
import {useColumnWidth} from '../../../../state-providers/columns/use-column-width'
import {getColumnBehaviors} from '../../column-behaviors'
import type {TableDataType} from '../../table-data-type'
import type {ReactTableColumnBehavior} from './types'

type TableColumns = {
  /** Column definitions for use with react-table */
  columnDefinitions: Array<UseTableColumnOptions<TableDataType>>

  /** Array of columns that should be hidden from the user */
  hiddenColumns: Array<string>
}

export const TableColumnsContext = createContext<TableColumns | null>(null)

export const TableColumnsProvider = memo<{
  children: React.ReactNode
  isFieldVisible: (column: ColumnModel) => boolean
}>(function TableColumnsProvider({isFieldVisible, children}) {
  const {allColumns} = useAllColumns()
  const {getWidth} = useColumnWidth()

  const columnDefinitions: Array<UseTableColumnOptions<TableDataType>> = useMemo(() => {
    return allColumns.map(column => build(column, getWidth(column.id)))
  }, [allColumns, getWidth])

  // For the roadmap view, "visible fields" do not currently control the columns shown in the view.
  const hiddenColumns = useMemo(() => {
    return allColumns.filter(col => !isFieldVisible(col)).map(col => `${col.id}`)
  }, [allColumns, isFieldVisible])

  const contextValue = useMemo(() => {
    return {columnDefinitions, hiddenColumns}
  }, [columnDefinitions, hiddenColumns])

  return <TableColumnsContext.Provider value={contextValue}>{children}</TableColumnsContext.Provider>
})

/** Lookup and create the column definition for use in react-table */
export function build(column: ColumnModel, width: number | undefined): UseTableColumnOptions<TableDataType> {
  const definition = getColumnBehaviors(column)
  return buildWith(column, definition, width)
}

/**
 * Combine the column behaviour with the column model to generate the column
 * behavior for react-table
 */
function buildWith(
  column: ColumnModel,
  columnBehavior: ReactTableColumnBehavior,
  width: number | undefined,
): UseTableColumnOptions<TableDataType> {
  return {
    Header: column.name,
    id: column.id.toString(),
    minWidth: 75,
    width,
    columnModel: column,
    ...columnBehavior,
  }
}

try{ TableColumnsContext.displayName ||= 'TableColumnsContext' } catch {}
try{ TableColumnsProvider.displayName ||= 'TableColumnsProvider' } catch {}